<script setup>
import { computed } from 'vue'
import { useRoute, createError, useHead, useNuxtApp } from '#imports'
import berlinLocations from '~/data/berlin'
import HeroSection from '~/components/landing/HeroSection.vue'
import EventTypesSection from '~/components/landing/EventTypesSection.vue'
import FeaturesSection from '~/components/landing/FeaturesSection.vue'
import GallerySection from '~/components/landing/GallerySection.vue'
import PackagesSection from '~/components/landing/PackagesSection.vue'
import FaqSection from '~/components/landing/FaqSection.vue'
import ProcessSection from '~/components/landing/ProcessSection.vue'
import LocationSection from '~/components/landing/LocationSection.vue'
import SiteFooter from '~/components/SiteFooter.vue'
import { getDistrictEventTypes } from '~/data/districtEventTypes'
import ContactForm from '~/components/landing/ContactForm.vue'

const route = useRoute()
const routeDistrict = Array.isArray(route.params.district) ? route.params.district[0] : route.params.district
const locationKey = `berlin-${String(routeDistrict || '').replace(/^berlin-/, '')}`
const location = berlinLocations[locationKey]

if (!routeDistrict || !location) {
  throw createError({ statusCode: 404, message: 'Location not found' })
}

const district = {
  slug: location.district,
  name: location.name,
  description: location.description || `Professionelle Fotobox für Ihre Veranstaltung in ${location.name}.`,
  venues: location.venues?.slice(0, 3) || [],
  eventTypes: getDistrictEventTypes('berlin')
}

const pageTitle = `Fotobox mieten in ${district.name} | Alle Eventarten`
const pageDescription = district.description
const canonicalUrl = `https://mietfotobox.com/berlin/${district.slug}/`

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogDescription: pageDescription,
  ogImage: 'https://mietfotobox.com/static/assets/hero.webp',
  canonical: canonicalUrl
})

const { $generateBreadcrumbSchema } = useNuxtApp()

const breadcrumbData = computed(() =>
  $generateBreadcrumbSchema([
    {
      name: 'mietfotobox.com',
      item: 'https://mietfotobox.com/'
    },
    {
      name: 'Berlin',
      item: 'https://mietfotobox.com/berlin/'
    },
    {
      name: district.name,
      item: canonicalUrl
    }
  ])
)

useHead(() => ({
  script: breadcrumbData.value && Object.keys(breadcrumbData.value).length > 0
    ? [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(breadcrumbData.value)
        }
      ]
    : []
}))

const faqItems = computed(() => [
  {
    question: `Welche Eventarten bieten Sie in ${district.name} an?`,
    answer: `In ${district.name} können Sie für Hochzeit, Geburtstag, Firmenfeier, Jugendweihe und vieles mehr direkt anfragen.`
  },
  {
    question: `Ist die Fotobox in ${district.name} inklusive Aufbau verfügbar?`,
    answer: `Ja. Lieferung, Aufbau und Abholung werden für Veranstaltungen in ${district.name} gemeinsam mit Ihnen abgestimmt.`
  },
  {
    question: `Kann ich für ${district.name} ein individuelles Angebot anfragen?`,
    answer: `Ja. Über das Anfrageformular können Sie Anlass, Datum, Gästezahl und Wunschpaket direkt übermitteln.`
  },
  {
    question: `Eignet sich die Fotobox für verschiedene Locations in ${district.name}?`,
    answer: `Ja. Wir planen die Aufstellung passend zu Ihrer Location, egal ob Restaurant, Eventfläche, Hotel oder private Feier.`
  }
])

const locationDescription = computed(() => {
  return location.locationDescription || `${district.description} Wir beraten Sie zu Aufstellung, Ablauf und passendem Paket für Ihre Veranstaltung in ${district.name}.`
})
</script>

<template>
  <div>
    <HeroSection
      :title="`Fotobox mieten in ${district.name}`"
      :intro-text="`Wählen Sie für ${district.name} direkt den passenden Anlass. Starten Sie jetzt Ihre Anfrage!`"
      :hero-image="location.heroImage || 'https://mietfotobox.com/static/assets/hero.webp'"
    />

    <EventTypesSection
      :location-name="district.name"
      :base-path="`/berlin/${district.slug}`"
      :event-types="district.eventTypes"
      :venues="district.venues"
    />

    <FeaturesSection :location-name="district.name" eventType="Veranstaltung" />
    <GallerySection :location-name="district.name" />
    <PackagesSection :location-name="district.name" />
    <div id="inquiry-form" class="bg-pink-700 px-4"></div>
    <ContactForm></ContactForm>
    <FaqSection :faq-items="faqItems" :location-name="district.name" />
    <ProcessSection :location-name="district.name" />
    <LocationSection :location-name="district.name" eventType="Veranstaltungen" :description="locationDescription" />
    <SiteFooter :current-location="district.name" :neighbor-locations="location.neighborLocations || []" />
  </div>
</template>
